import React from "react";
import {
  ToggleButtonGroup, 
  ToggleButton,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface ToggleOption {
  /** Unique value for this option */
  value: string;
  /** Display label */
  label: string;
  /**
   * Optional canonical metric id, used by the chart-area dispatcher to route
   * non-line "segment" metrics (trend, mastery status, …) to their card. Absent
   * for plain line-chart metrics.
   */
  id?: string;
}

interface ToggleChartGroupProps {
  /** The currently selected value */
  value: string;
  /** List of toggle options to render */
  options: ToggleOption[];
  /** Callback fired when the selected value changes */
  onChange: (value: string) => void;
  /** Accessible label for the group */
  "aria-label"?: string;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

/**
 * Pill-shaped toggle button group used to switch between chart views or
 * data display modes. Renders a row of small toggle buttons inside a
 * rounded container with a subtle border and shadow.
 *
 * @param props
 * @param props.value - The currently selected value
 * @param props.options - Array of { value, label } toggle options
 * @param props.onChange - Called with the new value when selection changes
 * @param [props.aria-label] - Accessible label for the group
 * @param [props.sx] - Additional MUI sx overrides
 */
const ToggleChartGroup: React.FC<ToggleChartGroupProps> = ({
  value,
  options,
  onChange,
  "aria-label": ariaLabel = "Toggle view",
  sx,
}) => {
  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      aria-label={ariaLabel}
      size="small"
      sx={sx}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default ToggleChartGroup;
export type { ToggleChartGroupProps, ToggleOption };
